import { Injectable } from '@angular/core';
import { ITaskParameter } from '@fi-shared/fi-task-shared/fi-task-shared';
import { IItemsListDataFetchResult } from '@fi-shared/fi-items-list/fi-items-list';
import { FiBackendService } from '@fi-shared/fi-backend/fi-backend.service';
import {
    ITask2StatusChangeItem,
    ITask2StatusChangeData,
    ITaskStatusChangeResult,
    ITaskV2,
    ITaskV2AssignedUser,
    ITaskV2Log,
    ITaskV2Overview,
    ITask2ArchivedChangeItem,
    ITaskArchivedChangeResult,
    ITaskChangeResultBase,
} from './fi-task-v2';
import { LoadingService } from '@fi-shared/fi-loading/loading.service';
import { FiTaskType } from './fi-task-v2-enums';
import { ActionType } from '@fi-shared/types';
import { FiTaskSyncService } from '@fi-shared/fi-task-shared/fi-task-sync-service';
import { IAccessRightFilterCollection } from '@fi-shared/models';
import { FiTaskStatusType } from './fi-task-v2-enums';

@Injectable()
export class FiTaskV2Service {
    constructor(
        private fiBackend: FiBackendService,
        private fiLoading: LoadingService,
        private fiTaskSyncService: FiTaskSyncService,
    ) {}

    getTasksListData(parameter: ITaskParameter): Promise<IItemsListDataFetchResult<ITaskV2>> {
        const promise = this.fiBackend.fetchDataPost<IItemsListDataFetchResult<ITaskV2>>(
            'Task/Task2/GetTasksListData',
            parameter,
        );
        return promise;
    }

    saveTask(task: ITaskV2): Promise<number> {
        return this.fiLoading.trackPromise(this.fiBackend.fetchDataPost<number>('Task/Task2/SaveTask', task));
    }

    getTask(taskId: number): Promise<ITaskV2> {
        return this.fiLoading.trackPromise(this.fiBackend.fetchData<ITaskV2>(`Task/Task2/GetTask/${taskId}`));
    }

    getTaskDetails(taskId: number): Promise<ITaskV2> {
        return this.fiLoading.trackPromise(this.fiBackend.fetchData<ITaskV2>(`Task/Task2/GetTaskDetails/${taskId}`));
    }

    getTaskOverview(taskId: number): Promise<ITaskV2Overview> {
        return this.fiLoading.trackPromise(this.getTaskOverviewNoTracking(taskId));
    }

    getTaskOverviewNoTracking(taskId: number): Promise<ITaskV2Overview> {
        return this.fiBackend.fetchData<ITaskV2Overview>(`Task/Task2/GetTaskOverview/${taskId}`);
    }

    updateTaskStatus(tasks: ITaskV2[], status: FiTaskStatusType, isUndo?: boolean): Promise<ITaskV2[]> {
        const promise = new Promise<ITaskV2[]>((resolve, reject) => {
            this.fiLoading
                .trackPromise(
                    this.simpleUpdateTaskStatus(
                        tasks.map((task) => ({ Id: task.Id, Status: status })),
                        isUndo,
                    ),
                )
                .then(() => {
                    const newTasks = tasks.map((task) => {
                        const newTask: ITaskV2 = { ...task, Status: status };
                        return newTask;
                    });

                    this.fiTaskSyncService.updateTasksV2ToBeDone(newTasks);
                    resolve(newTasks);
                }, reject);
        });

        return promise;
    }

    // use this property for deleting task logs on undo
    private latestTaskLogChangeResults: ITaskChangeResultBase[] = [];
    simpleUpdateTaskStatus(items: ITask2StatusChangeData[], isUndo: boolean) {
        const taskChangeItems: ITask2StatusChangeItem[] = items.map((item) => ({
            Id: item.Id,
            Status: item.Status,
            IsUndo: isUndo,
            TaskLogIdToUndo: isUndo
                ? this.latestTaskLogChangeResults.find((changeResult) => changeResult.TaskId === item.Id)?.TaskLogId
                : undefined,
            TaskPersonalNotificationIdsToUndo: isUndo
                ? (this.latestTaskLogChangeResults.find((changeResult) => changeResult.TaskId === item.Id)
                      ?.PersonalNotificationIds ?? [])
                : [],
        }));
        const promise = this.fiBackend.fetchDataPost<ITaskStatusChangeResult[]>(
            `Task/Task2/SaveTaskStatus`,
            taskChangeItems,
        );
        promise.then((result) => {
            this.latestTaskLogChangeResults = result;
        });
        return promise;
    }

    saveTaskArchived(tasks: ITaskV2[], isArchived: boolean): Promise<void> {
        return this.fiLoading.trackPromise(
            this.simpleArchiveTask(
                tasks.map((task) => task.Id),
                isArchived,
            ).then(() => {
                tasks.forEach((task) => {
                    if (isArchived) {
                        task.IsArchived = true;
                        task.ArchivedOn = new Date().toISOString();
                    } else {
                        task.IsArchived = false;
                        task.ArchivedOn = null;
                    }
                });
                this.fiTaskSyncService.updateTasksV2ToBeDone(tasks);
            }),
        );
    }

    simpleArchiveTask(taskIds: number[], isArchived: boolean, isUndo = false): Promise<ITaskArchivedChangeResult[]> {
        const taskChangeItems: ITask2ArchivedChangeItem[] = taskIds.map((taskId) => ({
            Id: taskId,
            IsArchived: isArchived,
            IsUndo: isUndo,
            TaskLogIdToUndo: isUndo
                ? this.latestTaskLogChangeResults.find((changeResult) => changeResult.TaskId === taskId)?.TaskLogId
                : undefined,
            TaskPersonalNotificationIdsToUndo: isUndo
                ? (this.latestTaskLogChangeResults.find((changeResult) => changeResult.TaskId === taskId)
                      ?.PersonalNotificationIds ?? [])
                : [],
        }));

        const promise = this.fiBackend.fetchDataPost<ITaskArchivedChangeResult[]>(
            `Task/Task2/SaveTaskArchived`,
            taskChangeItems,
        );
        promise.then((result) => {
            this.latestTaskLogChangeResults = result;
        });
        return this.fiLoading.trackPromise(promise);
    }

    deleteTasks(taskIds: number[], isTaskV2): Promise<void> {
        const url = isTaskV2 ? `Task/Task2/DeleteTasks` : `Task/Task/DeleteTasks`;
        return this.fiLoading.trackPromise(this.fiBackend.fetchDataPost<void>(url, taskIds));
    }

    getTaskLog(taskId: number): Promise<ITaskV2Log[]> {
        return this.fiLoading.trackPromise(this.fiBackend.fetchData<ITaskV2Log[]>(`Task/Task2/GetTaskLog/${taskId}`));
    }

    sendReminder(taskId: number): Promise<void> {
        return this.fiLoading.trackPromise(
            this.fiBackend.fetchDataPost<void>(`Task/Task2/SendTaskReminders`, [taskId]),
        );
    }

    getAvailableUsersForAssignee(): Promise<ITaskV2AssignedUser[]> {
        const promise = this.fiBackend.fetchData<ITaskV2AssignedUser[]>(`Task/Task2/GetAvailableUsersForAssignee`);
        return this.fiLoading.trackPromise(promise);
    }

    addTaskAssignees(taskId: number, userIds: number[]): Promise<void> {
        return this.fiLoading.trackPromise(
            this.fiBackend.fetchDataPost<void>(`Task/Task2/ChangeTaskAssignees`, {
                taskId: taskId,
                userIds: userIds,
                actionType: ActionType.Create,
            }),
        );
    }

    getTaskAccessRightFilterCollection(taskType: FiTaskType, isTaskV2: boolean) {
        const url = `Task/Task2/GetTaskAccessRightFilterCollection?taskType=${taskType}&isTaskV2=${isTaskV2}`;
        return this.fiLoading.trackPromise(this.fiBackend.fetchData<IAccessRightFilterCollection>(url));
    }
}
